import { useState, useRef, useEffect } from 'react';

export const usePrint = () => {
    const [isGenerating, setIsGenerating] = useState(false);
    const printResourcesRef = useRef([]);

    const printPDF = async (serviceCall) => {
        let url;
        try {
            setIsGenerating(true);
            const response = await serviceCall();
            console.log('Print response received', response);
            
            const blob = response.data ? response.data : response;
            console.log('Blob type:', blob?.type);
            console.log('Blob size:', blob?.size);
            
            if (!(blob instanceof Blob)) {
                throw new Error('Response is not a valid Blob');
            }
            
            url = URL.createObjectURL(blob);
            console.log('Blob URL created:', url);
            
            // For PDFs, the most reliable approach is to open in a new tab
            // Browser's native PDF viewer handles printing better than iframe
            const printWindow = window.open(url, '_blank');
            
            if (!printWindow) {
                throw new Error('Failed to open print window. Please allow popups for this site.');
            }
            
            console.log('PDF opened in new tab');
            
            // Clean up the blob URL after a delay
            setTimeout(() => {
                URL.revokeObjectURL(url);
                console.log('Blob URL revoked');
            }, 60000); // 1 minute - give user time to print
            
        } catch (error) {
            console.error('Print error:', error);
            if (url) {
                URL.revokeObjectURL(url);
            }
            throw error;
        } finally {
            setIsGenerating(false);
        }
    };

    const printSimpleHTML = () => {
        console.log('Starting simple HTML print test...');
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.top = '0';
        iframe.style.left = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        iframe.style.zIndex = '9999';
        iframe.style.opacity = '0';
        
        document.body.appendChild(iframe);
        
        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Test Print</title>
                <style>
                    body {
                        font-family: Arial, sans-serif;
                        padding: 20px;
                    }
                    h1 {
                        color: #333;
                    }
                    .test-content {
                        margin: 20px 0;
                        padding: 20px;
                        border: 2px solid #333;
                    }
                </style>
            </head>
            <body>
                <h1>Print Test Document</h1>
                <div class="test-content">
                    <p>This is a simple HTML print test.</p>
                    <p>If you see this in the print dialog, the print mechanism works!</p>
                    <p>Date: ${new Date().toLocaleString()}</p>
                </div>
            </body>
            </html>
        `;
        
        iframe.onload = () => {
            console.log('Simple HTML iframe loaded');
            try {
                const doc = iframe.contentDocument || iframe.contentWindow.document;
                doc.open();
                doc.write(htmlContent);
                doc.close();
                
                // Wait a bit for content to render
                setTimeout(() => {
                    console.log('Attempting to print simple HTML...');
                    iframe.contentWindow.focus();
                    iframe.contentWindow.print();
                    console.log('Print dialog triggered');
                    
                    // Clean up after print
                    setTimeout(() => {
                        if (document.body.contains(iframe)) {
                            document.body.removeChild(iframe);
                        }
                    }, 1000);
                }, 100);
            } catch (e) {
                console.error('Simple HTML print failed:', e);
                if (document.body.contains(iframe)) {
                    document.body.removeChild(iframe);
                }
            }
        };
        
        // For simple HTML, we can use about:blank
        iframe.src = 'about:blank';
    };

    const cleanup = () => {
        printResourcesRef.current.forEach(({ url, iframe }) => {
            URL.revokeObjectURL(url);
            if (iframe && document.body.contains(iframe)) {
                document.body.removeChild(iframe);
            }
        });
        printResourcesRef.current = [];
    };

    return {
        printPDF,
        printSimpleHTML,
        isGenerating,
        cleanup
    };
};